import React from 'react';
import type { Bygg } from '@forvalter/types/ForvalterCommonDAL';
import type { Theme } from '@material-ui/core';
import {
    Avatar,
    IconButton,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Typography } from '@klp/design/Typography';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import classNames from 'classnames';
import { Tooltip } from '@klp/design/Tooltip';

type ByggSelectorItemProps = {
    bygg: Bygg;
    openMineBygg: boolean;
    disabled: boolean;
    onSelect: (bygg: Bygg) => void;
    onDelete: (favorittId: Bygg['favorittId']) => void;
};

const styles = (theme: Theme) => ({
    listItem: {
        '& .ByggSelectorItem__Container': {
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'transparent',
            paddingTop: theme.spacing(0.5),
            paddingBottom: theme.spacing(0.5),
            paddingRight: theme.spacing(0.5),
            paddingLeft: theme.spacing(0.5),
            cursor: 'pointer',
            transition: theme.byggSelector.transition,
        },
        '& .ByggSelectorItem__Icon': {
            minWidth: `${theme.spacing(7)}px !important`,
            '& > button': {
                width: theme.spacing(7),
                height: theme.spacing(7),
            },
        },
        '& .MuiListItemText-root': {
            paddingLeft: theme.spacing(0.5),
            paddingRight: theme.spacing(0.5),
            visibility: 'hidden',
            transition: theme.byggSelector.transition,
        },
    },
    listItemOpen: {
        '& .ByggSelectorItem__Container': {
            borderColor: theme.palette.secondary.main,
            paddingRight: theme.spacing(0.5),

            '&:hover': {
                backgroundColor: theme.palette.secondary.veryLight,
            },
        },
        '& .MuiListItemText-root': {
            visibility: 'visible',
        },
    },
    avatarWrapper: {
        width: theme.spacing(6.5),
        height: theme.spacing(6.5),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50%',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: theme.palette.primary.mediumLight,
    },
    avatar: {
        width: theme.spacing(7),
        height: theme.spacing(7),
    },
    favouriteIcon: {
        // minWidth: theme.spacing(5),
    },
});
export const ByggSelectorItem = React.memo<ByggSelectorItemProps>(
    ({ bygg, openMineBygg, disabled, onSelect, onDelete }) => {
        const classes = useKlpStyles({}, styles);
        return (
            <ListItem
                key={bygg.id}
                className={classNames(
                    'ByggSelector__ListItem',
                    classes.listItem,
                    {
                        [classes.listItemOpen]: openMineBygg,
                    },
                )}
                disableGutters={true}
            >
                <div className="ByggSelectorItem__Container">
                    <ListItemIcon
                        className={classNames(
                            'ByggSelectorItem__Icon',
                            classes.favouriteIcon,
                        )}
                    >
                        <IconButton
                            color="primary"
                            onClick={(e) => {
                                onDelete(bygg?.favorittId);
                                e.stopPropagation();
                            }}
                            disabled={disabled}
                        >
                            <Illustration
                                icon={IconName.STAR_FILLED}
                                size="xsmall"
                                klpPalette="secondary"
                                disabled={disabled}
                            />
                        </IconButton>
                    </ListItemIcon>
                    <ListItemText
                        primary={
                            <Typography klpTypographyVariant="xsmall">
                                {bygg.navn}
                            </Typography>
                        }
                        onClick={() => onSelect(bygg)}
                    />
                    <Tooltip title={bygg?.navn ?? ''}>
                        <div
                            className={classes.avatarWrapper}
                            onClick={() => onSelect(bygg)}
                        >
                            {bygg?.hovedVisningsBilde && (
                                <Avatar
                                    src={bygg.hovedVisningsBilde}
                                    className={classes.avatar}
                                />
                            )}
                            {!bygg?.hovedVisningsBilde && (
                                <Illustration
                                    size="medium"
                                    icon={IconName.HOUSE}
                                    klpPalette="secondary"
                                    klpColor="main"
                                />
                            )}
                        </div>
                    </Tooltip>
                </div>
            </ListItem>
        );
    },
);

ByggSelectorItem.displayName = 'ByggSelectorItem';
